// /app/frontend/src/pages/TrackingPage.jsx
// Live tracking page for customers to see installer location and ETA

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  MapPin, Clock, User, Navigation, CheckCircle, 
  XCircle, Loader2, RefreshCw, Phone, Home, Truck
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function TrackingPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [trackingData, setTrackingData] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchTracking = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const response = await axios.get(`${API}/api/track/${token}`);
      setTrackingData(response.data);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Tracking link not found or has expired.');
      } else {
        setError('Unable to load tracking information. Please try again.');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token]);

  useEffect(() => {
    fetchTracking();
    
    // Auto-refresh every 30 seconds for en_route status
    const interval = setInterval(() => {
      if (trackingData?.status === 'en_route') {
        fetchTracking();
      }
    }, 30000);
    
    return () => clearInterval(interval);
  }, [fetchTracking, trackingData?.status]);

  const getStatusInfo = (status) => {
    const statusMap = {
      'scheduled': { 
        icon: Clock, 
        color: 'bg-blue-100 text-blue-700',
        label: 'Scheduled',
        description: 'Your appointment is confirmed'
      },
      'en_route': { 
        icon: Truck, 
        color: 'bg-amber-100 text-amber-700',
        label: 'On The Way',
        description: 'Your technician is heading to you'
      },
      'in_progress': { 
        icon: User, 
        color: 'bg-green-100 text-green-700',
        label: 'In Progress',
        description: 'Work is underway'
      },
      'completed': { 
        icon: CheckCircle, 
        color: 'bg-green-100 text-green-700',
        label: 'Completed',
        description: 'Job completed successfully'
      },
      'cancelled': { 
        icon: XCircle, 
        color: 'bg-red-100 text-red-700',
        label: 'Cancelled',
        description: 'This appointment was cancelled'
      }
    };
    return statusMap[status] || statusMap['scheduled'];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-amber-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading tracking information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="h-8 w-8 text-red-500" />
            </div>
            <h2 className="text-xl font-semibold mb-2">Tracking Unavailable</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <Button onClick={() => navigate('/')} className="bg-amber-600 hover:bg-amber-700">
              <Home className="h-4 w-4 mr-2" />
              Return Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statusInfo = getStatusInfo(trackingData?.status);
  const StatusIcon = statusInfo.icon;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 py-6 px-4">
      <div className="max-w-lg mx-auto space-y-4">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Apollo Shades</h1>
          <p className="text-gray-500">Live Tracking</p>
        </div>

        {/* Status Card */}
        <Card className="overflow-hidden">
          <div className={`p-6 ${statusInfo.color.replace('text-', 'bg-').replace('-700', '-500')} text-white`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-90">Current Status</p>
                <h2 className="text-2xl font-bold flex items-center gap-2">
                  <StatusIcon className="h-6 w-6" />
                  {statusInfo.label}
                </h2>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => fetchTracking(true)}
                disabled={refreshing}
                className="text-white hover:bg-white/20"
                data-testid="refresh-tracking-btn"
              >
                <RefreshCw className={`h-5 w-5 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
          
          <CardContent className="p-6">
            <p className="text-gray-600 mb-4">{statusInfo.description}</p>
            
            {/* ETA Section */}
            {trackingData?.eta && trackingData.status === 'en_route' && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                    <Clock className="h-6 w-6 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm text-amber-700">Estimated Arrival</p>
                    <p className="text-2xl font-bold text-amber-800">
                      {trackingData.eta.text || `${trackingData.eta.minutes} minutes`}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Technician Info */}
            {trackingData?.installer_name && (
              <div className="flex items-center gap-3 py-3 border-b">
                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                  <User className="h-5 w-5 text-gray-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Your Technician</p>
                  <p className="font-medium">{trackingData.installer_name}</p>
                </div>
              </div>
            )}

            {/* Appointment Details */}
            {trackingData?.event_title && (
              <div className="flex items-start gap-3 py-3 border-b">
                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mt-1">
                  <Navigation className="h-5 w-5 text-gray-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Service</p>
                  <p className="font-medium">{trackingData.event_title}</p>
                </div>
              </div>
            )}

            {/* Destination */}
            {trackingData?.destination?.address && (
              <div className="flex items-start gap-3 py-3">
                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mt-1">
                  <MapPin className="h-5 w-5 text-gray-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Destination</p>
                  <p className="font-medium">{trackingData.destination.address}</p>
                  {(trackingData.destination.city || trackingData.destination.state) && (
                    <p className="text-sm text-gray-600">
                      {[trackingData.destination.city, trackingData.destination.state, trackingData.destination.zip]
                        .filter(Boolean).join(', ')}
                    </p>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Timeline Card - for en_route or in_progress */}
        {['en_route', 'in_progress'].includes(trackingData?.status) && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Progress</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="relative">
                <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                
                {/* Departed */}
                <div className="relative flex items-start gap-4 pb-6">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center z-10 ${
                    trackingData.en_route_at ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-400'
                  }`}>
                    <Truck className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="font-medium">Departed</p>
                    {trackingData.en_route_at && (
                      <p className="text-sm text-gray-500">
                        {new Date(trackingData.en_route_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    )}
                  </div>
                </div>

                {/* Arrived */}
                <div className="relative flex items-start gap-4 pb-6">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center z-10 ${
                    trackingData.started_at ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-400'
                  }`}>
                    <MapPin className="h-4 w-4" />
                  </div>
                  <div>
                    <p className={`font-medium ${!trackingData.started_at ? 'text-gray-400' : ''}`}>
                      Arrived
                    </p>
                    {trackingData.started_at && (
                      <p className="text-sm text-gray-500">
                        {new Date(trackingData.started_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    )}
                  </div>
                </div>

                {/* In Progress */}
                <div className="relative flex items-start gap-4">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center z-10 ${
                    trackingData.status === 'in_progress' ? 'bg-amber-500 text-white animate-pulse' : 'bg-gray-200 text-gray-400'
                  }`}>
                    <User className="h-4 w-4" />
                  </div>
                  <div>
                    <p className={`font-medium ${trackingData.status !== 'in_progress' ? 'text-gray-400' : ''}`}>
                      Work in Progress
                    </p>
                    {trackingData.status === 'in_progress' && (
                      <p className="text-sm text-amber-600">Currently working...</p>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Completed Message */}
        {trackingData?.status === 'completed' && (
          <Card className="border-green-200 bg-green-50">
            <CardContent className="p-6 text-center">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-green-800">Job Completed!</h3>
              <p className="text-green-700 mt-2">
                Your installation has been completed successfully.
              </p>
              {trackingData.completed_at && (
                <p className="text-sm text-green-600 mt-2">
                  Completed at {new Date(trackingData.completed_at).toLocaleString()}
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Contact */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Phone className="h-5 w-5 text-gray-500" />
                <span className="text-gray-600">Need help?</span>
              </div>
              <a 
                href="tel:+12122090654" 
                className="text-amber-600 font-medium hover:underline"
              >
                (212) 209-0654
              </a>
            </div>
          </CardContent>
        </Card>

        {/* Last Updated */}
        {lastUpdated && (
          <p className="text-center text-xs text-gray-400">
            Last updated: {lastUpdated.toLocaleTimeString()}
          </p>
        )}
      </div>
    </div>
  );
}
